import { Link, useNavigate } from '@tanstack/react-router';
import { useForm } from '@/hooks/useForm.ts';
import { useSignIn, Credentials, MeResponse } from '@/hooks/useAuth.ts';
import { useAuthStore, AuthState } from '@/stores/useAuthStore';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { AppButton, FormField, TextInput } from "@/components/ui/primitives";
import { consumeAuthReturnDestination } from "@/utils/authReturnDestination";

export const SignInView = () => {
    const { t } = useTranslation(["auth", "common"]);
    const navigate = useNavigate();
    const token = useAuthStore((state: AuthState) => state.token);
    const signIn = useSignIn() as import('@tanstack/react-query').UseMutationResult<MeResponse, unknown, Credentials, unknown>;

    const { formState, onInputChange } = useForm<{ email: string, password: string }>(
        { email: '', password: '' }
    )
    const { email, password } = formState;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const hasRoutedAfterSignIn = useRef(false);

    const handleSignIn = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        setErrorMessage(null);
        try {
            const data = await signIn.mutateAsync({ email, password });
            if (!data.access_token) {
                throw new Error(t("common.errors.invalidCredentials"));
            }
        } catch (err: unknown) {
            const error = err as { response?: { data?: { detail?: string; error?: string } }; message?: string };
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.error ||
                error?.message ||
                t("common.errors.invalidCredentials");
            setErrorMessage(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (!token || hasRoutedAfterSignIn.current) return;
        hasRoutedAfterSignIn.current = true;
        const returnTo = consumeAuthReturnDestination();
        if (returnTo) window.location.assign(returnTo);
        else void navigate({ to: '/dashboard', replace: true });
    }, [token, navigate]);

    return (
        <div className="app-reveal w-full max-w-[440px] text-[var(--ds-ink)]">
            <div className="mb-6 text-left">
                <h1 className="font-[var(--ds-font-display)] text-[40px] font-medium leading-[1.1] tracking-[-0.03em]">{t("auth.signIn.title")}</h1>
                <p className="mt-3 leading-6 text-[var(--app-text-secondary)]">{t("auth.signIn.subtitle")}</p>
            </div>
            <form onSubmit={handleSignIn} className="mb-6 space-y-5">
                <FormField label={t("common.labels.email")} htmlFor="email" className="[&_label]:!font-medium">
                    <TextInput
                        id="email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={email}
                        placeholder="you@example.com"
                        onChange={onInputChange}
                        required
                    />
                </FormField>
                <FormField label={t("common.labels.password")} htmlFor="password" className="[&_label]:!font-medium">
                    <div className="relative">
                        <TextInput
                            id="password"
                            type={showPassword ? "text" : "password"}
                            name="password"
                            autoComplete="current-password"
                            value={password}
                            placeholder="********"
                            onChange={onInputChange}
                            inputClassName="pr-12"
                            required
                        />
                        <button
                            type="button"
                            className="absolute right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-[var(--app-text-secondary)] transition hover:bg-[var(--app-surface-hover)] hover:text-[var(--app-text)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--app-primary)_18%,transparent)]"
                            aria-label={showPassword ? t("common.actions.hidePassword") : t("common.actions.showPassword")}
                            aria-pressed={showPassword}
                            onClick={() => setShowPassword((value) => !value)}
                        >
                            {showPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                        </button>
                    </div>
                </FormField>
                <AppButton type="submit" tone="primary" size="md" fullWidth isLoading={isSubmitting} className="!font-medium !leading-none py-0 text-base md:min-h-11 md:py-0 md:text-base">
                    {isSubmitting ? t("common.states.signingIn") : t("auth.signIn.submit")}
                </AppButton>

                {errorMessage && (
                    <div role="alert" className="rounded-[var(--ds-radius-sm)] border border-[color:color-mix(in_srgb,var(--app-danger)_24%,var(--app-border)_76%)] bg-[var(--app-danger-soft)] px-3 py-2 text-sm text-[var(--app-danger)]">
                        {errorMessage}
                    </div>
                )}
            </form>
            <div className="flex flex-col gap-4 text-center">
                <div className="text-sm text-[var(--app-text-secondary)]">
                    <Link to="/auth/forgot-password" className="inline-flex min-h-11 items-center font-medium underline underline-offset-4 hover:text-[var(--app-text)]">
                        {t("auth.signIn.forgotPassword")}
                    </Link>
                </div>
                <div className="text-sm text-[var(--app-text-secondary)]">
                    {t("auth.signIn.noAccount")}{" "}
                    <Link to="/auth/signup" className="inline-flex min-h-11 items-center text-[15px] font-medium leading-[22px] text-[var(--app-primary)] hover:text-[var(--app-primary-hover)] hover:underline">
                        {t("auth.signIn.signUpLink")}
                    </Link>
                </div>
            </div>
        </div>
    );
};
